// REACT
import React from "react";

// MATERIAL-UI
import Drawer from "@mui/material/Drawer";
import {
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// COMPONENTS
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import CrossIcon from "src/icons/CrossIcon";
import { walletDrawerstyles } from "../common/styles/WalletDrawer.styles";
// import GA from "src/analytics/googleAnalytics";
import { BottomDrawerProps } from "../common/types/dropdownTypes";
import BottomDrawList from "./BottomDrawList";
import { useLocation } from "react-router-dom";
import usePermission from "src/utils/hooks/usePermission";

const initialFilter = [
  {
    change: 0,
    available: 0,
    price: 0,
    value: 0,
    pendingValue: 0,
    total: 0,
    openTargets: 0,
    reserved: 0,
    currency: {
      code: "",
      type: "",
      name: "",
      formatName: "",
      id: "",
      networkName: "",
      logoUrl: "",
      inactive: false,
      depositable: false,
      memo: false,
    },
  },
];

const errorMessage = "Only alphanumerical letters allowed";
const placeholder = "Search";

export function BottomDrawer(props: BottomDrawerProps) {
  const { config } = props;
  const { drawerStyles } = config;
  const classes = walletDrawerstyles();
  const location = useLocation();
  const [error, setError] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filter, setFilter] = React.useState<any>(initialFilter);
  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest";

  // used to allow guests to open the currency selection dropdown, but not other dropdowns
  const isCurrencySelection = config.subtitle === "Select Currency";

  // guard for guests and deposit components
  const canViewContent = !isGuest || isCurrencySelection;

  const validateInput = (e: any) => {
    if (!e.target.value.match(/^[a-zA-Z0-9 ]+$/)) {
      if (searchTerm.length === 1) {
        setSearchTerm("");
      } else {
        setError(true);
        e.target.value = searchTerm;
      }
    } else {
      setError(false);
    }
  };

  const loop = (all: any) => {
    let formattedData = [];

    for (let index in all) {
      let obj = {
        change: 0,
        available: 0,
        price: 0,
        value: 0,
        pendingValue: 0,
        total: 0,
        openTargets: 0,
        reserved: 0,
        code: "",
        type: "",
        name: "",
        formatName: "",
        id: "",
        networkName: "",
        logoUrl: "",
        inactive: false,
        depositable: false,
        memo: false,
      };
      obj["change"] = all[index].change;
      obj["available"] = all[index].available;
      obj["price"] = all[index].price;
      obj["value"] = all[index].value;
      obj["total"] = all[index].code;
      obj["code"] = all[index].code;
      obj["formatName"] = all[index].formatName;
      obj["logoUrl"] = all[index]?.logoUrl;
      obj["depositable"] = all[index].depositable;
      obj["memo"] = all[index].memo;
      obj["name"] = all[index].name;
      formattedData.push(obj);
    }

    return formattedData;
  };

  const handleFilter = (all: any) => {
    if (searchTerm.length <= 0) {
      setFilter(all);
      return;
    }
    const formattedData = loop(all);
    const newFilter = formattedData.filter((item) => {
      return Object.values(item).join(" ").toLowerCase().match(searchTerm.toLowerCase());
    });

    setFilter(newFilter);
  };

  const handleOnChange = (e: any) => {
    validateInput(e);
    setSearchTerm(e.target.value);
  };

  React.useEffect(() => {
    // GET TRENDING DATA ON INITIAL LOAD
    if (searchTerm.length <= 0) {
      setError(false);
    }

    handleFilter(config.data);
  }, [searchTerm]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    if (code) {
      setSearchTerm(code);
    }
  }, [location]);

  return (
    <Drawer
      anchor="bottom"
      open={canViewContent && config.drawerOpen}
      onClose={config.drawerClose}
      PaperProps={{
        sx: { borderRadius: drawerStyles, height: drawerStyles },
      }}>
      <Grid container className={classes.headerContainer}>
        <Grid item xs={10}>
          <Typography className={classes.currencyTitle}>{config.subtitle}</Typography>
        </Grid>
        <Grid item xs={2} display="flex" justifyContent="right">
          <IconButton id="BottomDrawer-close-button" onClick={config.drawerClose}>
            <CrossIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container className={classes.bottomDrawer} id="currency-selection-list">
        {config.searchable && (
          <Grid container className={classes.searchbarContainer}>
            <Grid item xs style={{ width: "100%", padding: "20px 25px 20px 20px" }}>
              <TextField
                variant="outlined"
                placeholder={placeholder}
                className={classes.searchbar}
                value={searchTerm}
                onChange={handleOnChange}
                error={!!error}
                type="text"
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      fontSize="medium"
                      color="disabled"
                      classes={{ root: classes.searchbarIcon }}
                    />
                  ),
                }}
              />
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                {/* ERROR */}
                {error && (
                  <div className={classes.error}>
                    <p>{errorMessage}</p>
                  </div>
                )}

                <Grid item container>
                  {config.data && searchTerm.length <= 0 && canViewContent ? (
                    <BottomDrawList
                      data={config.data}
                      onClick={isGuest ? () => {} : props.config.onClick}
                      searchTerm={searchTerm.length}
                    />
                  ) : (
                    <BottomDrawList
                      data={filter}
                      onClick={isGuest ? () => {} : config.onClick}
                      searchTerm={searchTerm.length}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {/* Fiat content */}
        {config.fiat &&
          canViewContent &&
          config.fiat.map((el) => (
            <List
              key={el.code}
              sx={{
                width: "100%",
                padding: "0px",
              }}>
              <ListItemButton
                onClick={(event) => {
                  if (!isGuest) {
                    config.onClick(event, el.code);
                  }
                }}>
                <span className={classes.icon}>
                  <CountryOrCryptoFlag name={el.code} size={30} />
                </span>
                <ListItemText
                  className={classes.listContainer}
                  primary={<Typography className={classes.Title}>{el.code}</Typography>}
                  secondary={<Typography>{el.name}</Typography>}
                />
              </ListItemButton>
            </List>
          ))}

        {config.data && !config.searchable && canViewContent && (
          <BottomDrawList
            data={config.data}
            onClick={isGuest ? () => {} : props.config.onClick}
            searchTerm={searchTerm.length}
          />
        )}
      </Grid>
    </Drawer>
  );
}
